import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { actions, actionTypes, selectors } from '@lbmx/app-state';
import { UserState } from '@lbmx/types';

import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { from, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  first,
  switchMap,
} from 'rxjs/operators';

import { ConfigProvider } from '../../provider/config-provider';
import _checkAuthorization from './authorization.check';
import _checkMarketplace from './marketplace.check';
import _checkTerms from './terms.check';

@Injectable()
export class AuthGuardService implements CanActivateChild, CanActivate {
  private dispatcher: {
    [key: string]: (userState: UserState, url?: string) => Observable<boolean | UrlTree>
  } = {
    checkTerms: ({ profile }) => _checkTerms(profile, { redirectUrl: '/terms', router: this.router }),
    checkAuthorization: ({ profile }, url) => _checkAuthorization(profile, { url }),
    checkMarketplace: (userState, url) => _checkMarketplace(userState, { url, router: this.router }),
  };

  constructor(
    private actions$: Actions,
    private configPrv: ConfigProvider,
    private router: Router,
    private store: Store
  ) {}

  public canActivateChild(
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this._isAuthorized(state.url, ['checkTerms', 'checkMarketplace', 'checkAuthorization']);
  }

  public canActivate(
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this._isAuthorized(state.url, ['checkAuthorization']);
  }

  private _isAuthorized(
    url: string,
    checks: string[]
  ): Observable<boolean | UrlTree> {
    return this.store.pipe(
      select(selectors.userFeature),
      switchMap((userState: UserState) => {
        return this.ensureProfile(userState, url, checks);
      }),
      catchError(() => of(this.router.parseUrl(`/home`)))
    );
  }

  public ensureProfile(
    userState: UserState,
    url: string,
    checks: string[]
  ): Observable<boolean | UrlTree> {
    return userState.profile.userInfo.IsTermsSigned === null ||
      new Date(userState.tokenExpires) < new Date()
      ? this.fetchUserProfile(url, checks)
      : this.checker(checks, userState, url);
  }

  private checker(
    checks: string[],
    userState: UserState,
    url: string
  ): Observable<boolean | UrlTree> {
    return from(checks).pipe(
      concatMap((check) => this.dispatcher[check](userState, url)),
      first((result) => result === false || result instanceof UrlTree, true)
    );
  }

  private fetchUserProfile(
    url: string,
    checks: string[]
  ): Observable<boolean | UrlTree> {
    this.refresh();
    return this.actions$.pipe(
      ofType(
        actions.refreshUserProfileSuccess,
        actions.refreshUserProfileFailure
      ),
      exhaustMap(
        (action): Observable<any> => {
          return of(
            action.type === actionTypes.REFRESH_USER_PROFILE_FAILURE
              ? this.router.parseUrl(`/login?nextUrl=${encodeURIComponent(url)}`)
              : this.checker(checks, action.userState, encodeURIComponent(url)) ||
                  this.router.parseUrl(`/home`)
          );
        }
      )
    );
  }
  private refresh() {
    this.store.dispatch(
      actions.refreshUserProfile({
        endPoints: this.configPrv.AppSetting,
      })
    );
  }
}
